import { useCallback, useEffect, useState } from 'react';

import { type Prize } from '@app/client/common/structs';
import { Currency } from '@app/client/common/structs/nft/currencies';
import { useFantasyMetadataStore } from '@app/store/zustand';

interface ValueCurrency {
    value: number;
    currency?: Currency;
}

function getValueCurrency(reward: string, providedCurrency: string): ValueCurrency {
    const parsedValue = parseFloat(reward);
    if (parsedValue) {
        return {
            value: parsedValue,
            currency: providedCurrency as Currency,
        };
    }

    // Old system
    const noCommas = reward.replace(/[, ]+/g, ' ').replace(/ /g, '');
    const value = noCommas.replace(/[a-z A-Z $ £]/g, '');
    const currencyStr = noCommas.replace(/[0-9.]/g, '');
    let currency: Currency | undefined = undefined;
    if (currencyStr === '$') {
        currency = Currency.USD;
    } else if (currencyStr === 'ETH') {
        currency = Currency.ETH;
    }
    return {
        value: parseFloat(value),
        currency,
    };
}

export const getPrizeTotals = (prizes: Prize[]) => {
    const valueCurrencies = prizes.map((prize) => getValueCurrency(prize.reward, prize.description));
    const total = valueCurrencies.reduce((acc, val) => acc + val.value, 0);
    return {
        value: Math.round(total * 100) / 100,
        currency: valueCurrencies[0].currency || Currency.USD,
    };
};

export function useGamePrizes() {
    const { currentGame } = useFantasyMetadataStore();
    const [topThreePrizes, setTopThreePrizes] = useState<Prize[]>([]);
    const [prizeTotal, setPrizeTotal] = useState<ValueCurrency>({ value: 0, currency: undefined });

    const handleTotal = useCallback((prizes: Prize[]) => {
        setPrizeTotal(getPrizeTotals(prizes));
    }, []);

    useEffect(() => {
        const prizes = currentGame?.prizes;
        if (prizes) {
            handleTotal(prizes);
        }

        const top3 = prizes?.slice(0, 3).sort((a, b) => a.rank - b.rank);
        setTopThreePrizes(top3 || []);
    }, [currentGame?.prizes, handleTotal]);

    return { topThreePrizes, prizeTotal };
}
